import React, { useState, useEffect } from "react";
import Menu from "../Menu";
import { useNavigate, Link } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from "recharts";

/* ─── Theme function ─────────────────────────── */
const getTheme = () => ({
  bg: 'var(--bg)',
  surface: 'var(--surface)',
  surface2: 'var(--surface2)',
  border: 'var(--border)',
  text: 'var(--text)',
  textMuted: 'var(--text-muted)',
  accent: 'var(--info)',
  info: 'var(--info)',
  warning: 'var(--warning)',
  danger: 'var(--danger)',
  accent2: 'var(--info2)',
  accent3: 'var(--info3)',
});

/* ─── Inline styles ─────────────────────────────────────────────────── */
const S = {
  wrapper: {
    background: 'var(--bg)',
    minHeight: '100vh',
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    color: 'var(--text)',
  },
  page: {
    padding: '24px 28px',
    maxWidth: 1300,
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    color: 'var(--text)',
    letterSpacing: '-0.3px',
  },
  breadcrumb: {
    fontSize: 13,
    color: 'var(--text-muted)',
    display: 'flex',
    gap: 6,
    alignItems: 'center',
  },
  breadLink: { color: 'var(--info)', textDecoration: 'none' },
  tabRow: {
    display: 'flex',
    gap: 6,
    background: 'var(--surface)',
    padding: '5px 6px',
    borderRadius: 12,
    width: 'fit-content',
    marginBottom: 24,
    border: '1px solid var(--border)',
  },
  tab: (active) => ({
    padding: '8px 22px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.18s ease',
    background: active ? 'var(--info)' : 'transparent',
    color: active ? '#0d1117' : 'var(--text-muted)',
    boxShadow: active ? '0 2px 12px rgba(16,185,129,0.35)' : 'none',
  }),
  selectRow: {
    display: 'flex',
    gap: 16,
    marginBottom: 28,
    flexWrap: 'wrap',
  },
  selectGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    flex: '1 1 200px',
    maxWidth: 300,
  },
  selectLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
  select: {
    padding: '10px 14px',
    borderRadius: 8,
    border: '1px solid var(--border)',
    background: 'var(--surface)',
    color: 'var(--text)',
    fontSize: 14,
    outline: 'none',
    cursor: 'pointer',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%2364748b' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 12px center',
    paddingRight: 36,
  },
  card: {
    background: 'var(--surface)',
    borderRadius: 16,
    border: '1px solid var(--border)',
    marginBottom: 20,
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '18px 22px 10px',
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: 700,
    color: 'var(--text)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  cardIcon: {
    width: 28,
    height: 28,
    background: 'rgba(16,185,129,0.15)',
    borderRadius: 6,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    color: 'var(--info)',
  },
  statGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: 12,
    padding: '0 22px 18px',
  },
  statCard: {
    background: 'var(--surface2)',
    borderRadius: 10,
    padding: '14px 16px',
    border: '1px solid var(--border)',
  },
  statLabel: { fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6 },
  statValue: { fontSize: 22, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.5px' },
  tableWrap: { overflowX: 'auto', padding: '0 22px 22px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: {
    padding: '10px 14px',
    textAlign: 'left',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  },
  thCenter: {
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--text-muted)',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid var(--border)',
    whiteSpace: 'nowrap',
  },
  tr: (i) => ({
    background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.015)',
    transition: 'background 0.15s',
  }),
  td: { padding: '14px 14px', color: 'var(--text)', borderBottom: '1px solid rgba(30,39,64,0.6)', verticalAlign: 'middle' },
  tdCenter: { padding: '14px 14px', textAlign: 'center', color: 'var(--text-muted)', borderBottom: '1px solid rgba(30,39,64,0.6)', verticalAlign: 'middle' },
  badge: (color) => ({
    width: 30,
    height: 30,
    borderRadius: '50%',
    background: color || 'rgba(99,102,241,0.2)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    color: '#fff',
    margin: '0 auto',
  }),
  teal: { color: 'var(--info)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  purple: { color: 'var(--info2)', fontWeight: 700, fontVariantNumeric: 'tabular-nums' },
  progressWrap: { display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'center' },
  progressBar: (pct) => ({
    width: 80,
    height: 6,
    borderRadius: 3,
    background: 'var(--border)',
    position: 'relative',
    overflow: 'hidden',
  }),
  progressFill: (pct) => ({
    position: 'absolute',
    left: 0, top: 0, bottom: 0,
    width: `${Math.min(pct, 100)}%`,
    background: pct >= 80 ? 'var(--info)' : pct >= 60 ? 'var(--warning)' : 'var(--danger)',
    borderRadius: 3,
  }),
  pctText: (pct) => ({
    fontSize: 12,
    fontWeight: 700,
    color: pct >= 80 ? 'var(--info)' : pct >= 60 ? 'var(--warning)' : 'var(--danger)',
    minWidth: 32,
  }),
  namePill: (color) => ({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: 20,
    background: color,
    fontWeight: 600,
    fontSize: 13,
    color: '#fff',
  }),
  emptyState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-muted)',
  },
  spinner: {
    display: 'inline-block',
    width: 28,
    height: 28,
    border: '3px solid var(--border)',
    borderTop: '3px solid var(--info)',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    marginBottom: 12,
  },
  noSelection: {
    textAlign: 'center',
    padding: '60px 20px',
    color: 'var(--text-muted)',
    background: 'var(--surface)',
    borderRadius: 16,
    border: '1px solid var(--border)',
  },
};

const PILL_COLORS = ['#b45309', '#065f46', '#1e3a5f', '#7f1d1d', '#4c1d95', '#0e7490', '#14532d', '#831843'];
const CHART_COLORS = { scheduled: 'var(--info2)', played: 'var(--info)' };

// Only 3 tabs shown on page load
const TABS = [
  { key: 'today', label: 'Today' },
  { key: 'thisweek', label: 'This Week' },
  { key: 'last7days', label: 'Last 7 Days' },
  { key: 'last30days', label: 'Last 30 Days' },
  { key: 'month', label: 'This Month' },
  { key: 'all',   label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

const AUTH_TOKEN = getCookie("token");
const API_BASE = 'https://api.dqr-display-tms.eventum.co.in/api/SchedulerStatistics';

const POSDashboard = () => {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('all');
  const [loading, setLoading] = useState(false);

  // Dropdown data
  const [POSS, setPOSS] = useState([]);
  const [schedulers, setSchedulers] = useState([]);
  const [dropdownLoading, setDropdownLoading] = useState(true);

  // Selections
  const [selectedPOS, setSelectedPOS] = useState('');
  const [selectedScheduler, setSelectedScheduler] = useState('');

  // Custom date states
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');

  // Report data
  const [Report12Data, setReport12] = useState(null);
  const [Report13Data, setReport13] = useState(null);
  const [Report14Data, setReport14Data] = useState(null);
  const [Report15Data, setReport15] = useState(null);
  const [Report16Data, setReport16] = useState(null);
  const [Report17Data, setReport17] = useState(null);

  // ── API helpers ────────────────────────────────────────────────────
  const callGetAPI = async (url) => {
    try {
      const res = await fetch(url, {
        method: 'GET',
        headers: { 'accept': 'text/plain', 'UserId': getCookie("userid"), 'X-Authorization': AUTH_TOKEN }
      });
      const responseText = await res.text();
      
      // Check if response is empty or invalid
      if (!responseText || responseText.trim() === '') {
        console.warn('Empty response from API:', url);
        return { isresponse: false };
      }
      
      // Try to parse as JSON
      try {
        return JSON.parse(responseText);
      } catch (parseError) {
        console.error('JSON parse error:', parseError, 'Response text:', responseText);
        return { isresponse: false };
      }
    } catch (e) {
      console.error('GET error:', e);
      return { isresponse: false };
    }
  };

  const callAPI = async (endpoint, payload) => {
    try {
      const res = await fetch(`${API_BASE}/${endpoint}`, {
        method: 'POST',
        headers: {
          'accept': 'text/plain',
          'UserId': getCookie("userid"),
          'X-Authorization': AUTH_TOKEN,
          'Content-Type': 'application/json-patch+json'
        },
        body: JSON.stringify(payload)
      });
      return JSON.parse(await res.text());
    } catch (e) {
      console.error(`POST ${endpoint} error:`, e);
      return { isresponse: false };
    }
  };

  // ── Date range builder ─────────────────────────────────────────────
  const getDateRange = (p) => {
    const now = new Date();
    if (p === 'all') return { startdate: 0, enddate: 0, isalltime: true };
    if (p === 'custom') {
      if (customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        end.setHours(23, 59, 59, 999); // End of day
        return { 
          startdate: Math.floor(start.getTime() / 1000), 
          enddate: Math.floor(end.getTime() / 1000), 
          isalltime: false 
        };
      }
      return { startdate: 0, enddate: 0, isalltime: false };
    }
    const endOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    const enddate = Math.floor(endOfDay.getTime() / 1000);
    let startdate;
    if (p === 'today') {
      startdate = Math.floor(new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime() / 1000);
    } else if (p === 'thisweek') {
      // Get current week (Sunday to Saturday)
      const dayOfWeek = now.getDay(); // 0 = Sunday, 6 = Saturday
      const sunday = new Date(now);
      sunday.setDate(now.getDate() - dayOfWeek);
      sunday.setHours(0, 0, 0, 0);
      startdate = Math.floor(sunday.getTime() / 1000);
    } else if (p === 'last7days') {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    } else if (p === 'last30days') {
      startdate = Math.floor((now.getTime() - 30 * 86400000) / 1000);
    } else if (p === 'month') {
      startdate = Math.floor(new Date(now.getFullYear(), now.getMonth(), 1).getTime() / 1000);
    } else {
      startdate = Math.floor((now.getTime() - 7 * 86400000) / 1000);
    }
    return { startdate, enddate, isalltime: false };
  };

  // ── On page load: fetch only the 2 dropdowns ──────────────────────
  useEffect(() => {
    const loadDropdowns = async () => {
      setDropdownLoading(true);
      const [posRes, schedRes] = await Promise.all([
        callGetAPI('https://api.dqr-display-tms.eventum.co.in/api/POSOperation/GetDistinct/posname'),
        callGetAPI('https://api.dqr-display-tms.eventum.co.in/api/CatalogSchedulerOperation/GetDistinct/name'),
      ]);
      if (posRes.isresponse && posRes.data) setPOSS(posRes.data);
      if (schedRes.isresponse && schedRes.data) setSchedulers(schedRes.data);
      setDropdownLoading(false);
    };
    loadDropdowns();
  }, []);

  // ── Load report data based on current selections ──────────────────
  const loadReports = async (pos, scheduler, p) => {
    if (!pos && !scheduler) {
      // No selection — clear all report data, show empty state
      setReport12(null); setReport13(null); setReport14Data(null);
      setReport15(null); setReport16(null); setReport17(null);
      return;
    }

    setLoading(true);
    const range = getDateRange(p);

    if (scheduler) {
      // Scheduler selected → Report15, Report16, Report17
      const payload = {
        ...range,
        scheduleid: parseInt(scheduler),
        posid: pos ? parseInt(pos) : 0,
      };
      const [r15, r16, r17] = await Promise.all([
        callAPI('Report15', payload),
        callAPI('Report16', payload),
        callAPI('Report17', payload),
      ]);
      setReport15(r15); setReport16(r16); setReport17(r17);
      setReport12(null); setReport13(null); setReport14Data(null);
    } else if (pos) {
      // POS only → Report12, Report13, Report14
      const payload = { ...range, posid: parseInt(pos) };
      const [r12, r13, r14] = await Promise.all([
        callAPI('Report12', payload),
        callAPI('Report13', payload),
        callAPI('Report14', payload),
      ]);
      setReport12(r12); setReport13(r13); setReport14Data(r14);
      setReport15(null); setReport16(null); setReport17(null);
    }

    setLoading(false);
  };

  // Re-load when period changes (only if something is selected)
  useEffect(() => {
    if (selectedPOS || selectedScheduler) {
      loadReports(selectedPOS, selectedScheduler, period);
    }
  }, [period]);

  const handlePOSChange = (val) => {
    setSelectedPOS(val);
    loadReports(val, selectedScheduler, period);
  };

  const handleSchedulerChange = (val) => {
    setSelectedScheduler(val);
    loadReports(selectedPOS, val, period);
  };

  // ── Helpers ────────────────────────────────────────────────────────
  const secToHHMM = (s) => {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    const m = Math.floor((s % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };
  const fmt = (n) => (n ?? 0).toLocaleString();

  // Determine active report set
  const useSchedulerReports = !!selectedScheduler;
  const kpi = useSchedulerReports
    ? Report15Data?.data?.[0] || {}
    : Report12Data?.data?.[0] || {};
  const tableData = useSchedulerReports
    ? Report17Data?.data || Report16Data?.data || []
    : Report14Data?.data || Report13Data?.data || [];
  const chartData = useSchedulerReports
    ? Report16Data?.data || []
    : Report13Data?.data || [];

  // Process chart data based on report type
  const processedChartData = React.useMemo(() => {
    if (!chartData.length) return [];
    
    if (useSchedulerReports && Report16Data?.data?.length > 0) {
      // Report16: Group by catalogname and sum totalsecond
      const groupedData = {};
      
      chartData.forEach(item => {
        const name = item.catalogname || 'Unknown';
        if (!groupedData[name]) {
          groupedData[name] = {
            name: name,
            catalogid: item.catalogid,
            totalsecond: 0,
            avgrunningtimeinseconds: 0,
            count: 0
          };
        }
        groupedData[name].totalsecond += item.totalsecond || 0;
        groupedData[name].avgrunningtimeinseconds += item.avgrunningtimeinseconds || 0;
        groupedData[name].count += 1;
      });
      
      // Convert to array and calculate averages
      return Object.values(groupedData).map(item => ({
        name: item.name,
        scheduled: Math.round((item.totalsecond || 0) / 3600 * 10) / 10, // Convert to hours
        played: Math.round(((item.avgrunningtimeinseconds || 0) / item.count) / 3600 * 10) / 10, // Average in hours
      }));
    } else {
      // Report13/Report14: Single bar - avgrunningtimeinseconds
      return chartData.map(item => ({
        name: item.posname || item.name || 'Unknown',
        scheduled: Math.round((item.avgrunningtimeinseconds || 0) / 3600 * 10) / 10,
        played: Math.round((item.avgrunningtimeinseconds || 0) / 3600 * 10) / 10,
      }));
    }
  }, [chartData, useSchedulerReports, Report16Data]);

  const hasData = selectedPOS || selectedScheduler;
  const periodLabel = TABS.find(t => t.key === period)?.label || '';
  const isCatalogData = useSchedulerReports && Report16Data?.data?.length > 0;

  return (
    <>
      <Menu />
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        .cs-tr:hover td { background: rgba(0,123,255,0.04) !important; }
        .cs-select:focus { border-color: var(--primary) !important; box-shadow: 0 0 0 0.2rem rgba(0,123,255,0.25); }
        .cs-tab:hover { color: var(--text) !important; }
      `}</style>
      <div style={S.wrapper}>
        <div className="content-wrapper">
          <div style={S.page}>

            {/* Header */}
            <section className="content-header">
              <div className="container-fluid">
                <div className="row mb-2">
                  <div className="col-sm-6">
                    <h1 className="m-0">🏪 POS Dashboard</h1>
                  </div>
                  <div className="col-sm-6">
                    <ol className="breadcrumb float-sm-right">
                      <li className="breadcrumb-item">
                        <Link to="/" style={{ color: 'var(--primary)' }}>Home</Link>
                      </li>
                      <li className="breadcrumb-item active">POS Dashboard</li>
                    </ol>
                  </div>
                </div>
              </div>
            </section>

            <section className="content">
              <div className="container-fluid">
                
                {/* Period Tabs */}
                <div className="card mb-3">
                  <div className="card-body p-2">
                    <div className="btn-group w-100" role="group">
                      {TABS.map(t => (
                        <button
                          key={t.key}
                          type="button"
                          className={`btn ${period === t.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                          onClick={() => setPeriod(t.key)}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Two dropdowns */}
                <div className="row mb-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">Select POS</label>
                      <select
                        className="form-control cs-select"
                        value={selectedPOS}
                        onChange={e => handlePOSChange(e.target.value)}
                        disabled={dropdownLoading}
                      >
                        <option value="">All POS</option>
                        {POSS.map(pos => (
                          <option key={pos.id} value={pos.id}>{pos.value}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="form-label">Select Scheduler</label>
                      <select
                        className="form-control cs-select"
                        value={selectedScheduler}
                        onChange={e => handleSchedulerChange(e.target.value)}
                        disabled={dropdownLoading}
                      >
                        <option value="">All Schedulers</option>
                        {schedulers.map(s => (
                          <option key={s.id} value={s.id}>{s.value}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Dropdown loading */}
                {dropdownLoading && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <div className="text-muted mt-2">Loading options...</div>
                  </div>
                )}

                {/* No selection prompt */}
                {!dropdownLoading && !hasData && (
                  <div className="card">
                    <div className="card-body text-center py-5">
                      <div style={{ fontSize: 40, marginBottom: 16 }}>📊</div>
                      <h5 className="text-muted">Select a POS or Scheduler to view data</h5>
                      <div style={{ fontSize: 13, color: 'var(--text)' }}>
                        Choose from the dropdowns above to load dashboard reports
                      </div>
                    </div>
                  </div>
                )}

                {/* Data loading spinner */}
                {!dropdownLoading && hasData && loading && (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="sr-only">Loading...</span>
                    </div>
                    <div className="text-muted mt-2">Loading report data...</div>
                  </div>
                )}

            {/* Main content */}
                {!dropdownLoading && hasData && !loading && (
                  <>
                    {/* Summary Stats */}
                    <div className="row mb-4">
                      <div className="col-lg-3 col-6">
                        <div className="info-box">
                          <span className="info-box-icon bg-info">
                            <i className="fas fa-store"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">Total POS</span>
                            <span className="info-box-number">{fmt(POSS.length)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-6">
                        <div className="info-box">
                          <span className="info-box-icon bg-success">
                            <i className="fas fa-play"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">Total Play Time</span>
                            <span className="info-box-number">{secToHHMM(kpi.totalplaytime || 0)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-6">
                        <div className="info-box">
                          <span className="info-box-icon bg-primary">
                            <i className="fas fa-clock"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">Scheduled Time</span>
                            <span className="info-box-number">{secToHHMM(kpi.scheduledtime || 0)}</span>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-3 col-6">
                        <div className="info-box">
                          <span className="info-box-icon bg-warning">
                            <i className="fas fa-percentage"></i>
                          </span>
                          <div className="info-box-content">
                            <span className="info-box-text">Completion Rate</span>
                            <span className="info-box-number">
                              {kpi.scheduledtime ? Math.round((kpi.totalplaytime / kpi.scheduledtime) * 100) : 0}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                {/* Bar chart */}
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-chart-bar mr-2"></i>
                          {isCatalogData ? 'Catalogue Performance Overview' : 'POS Performance Overview'}
                          <small className="text-muted ml-2">
                            — {useSchedulerReports ? 'Scheduler Reports' : 'POS Reports'}
                          </small>
                        </h3>
                      </div>
                      <div className="card-body">
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={processedChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis />
                            <Tooltip 
                              formatter={(value, name) => [
                                `${value} hrs`, 
                                name === 'scheduled' ? 'Total Scheduled Time' : 'Avg. Running Time'
                              ]}
                            />
                            <Legend 
                              formatter={(value) => value === 'scheduled' ? 'Total Scheduled (hrs)' : 'Avg. Running (hrs)'}
                            />
                            <Bar dataKey="scheduled" fill="#007bff" name="scheduled" />
                            <Bar dataKey="played" fill="#28a745" name="played" />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    </div>

                    {/* Expected vs Actual Table */}
                    <div className="card">
                      <div className="card-header">
                        <h3 className="card-title">
                          <i className="fas fa-table mr-2"></i>
                          Expected vs Actual
                          <small className="text-muted ml-2">— {periodLabel}</small>
                        </h3>
                      </div>
                      <div className="card-body table-responsive p-0">
                        {tableData.length > 0 ? (
                          <table className="table table-hover table-striped">
                            <thead>
                              <tr>
                                <th className="text-center">#</th>
                                <th>{isCatalogData ? "Catalogue Name" : "POS Name"}</th>
                                <th className="text-center">Scheduled (HH:MM)</th>
                                <th className="text-center">Played (HH:MM)</th>
                                <th className="text-center">Completion %</th>
                                <th className="text-center">Interrupts</th>
                              </tr>
                            </thead>
                            <tbody>
                              {tableData.map((item, i) => {
                                const scheduled = isCatalogData 
                                  ? (item.scheduletotaltime || 0)
                                  : (item.scheduledtime || 0);
                                const played = isCatalogData
                                  ? (item.avgrunningtimeinseconds || 0)
                                  : (item.totalplaytime || 0);
                                const pct = scheduled > 0 ? Math.round((played / scheduled) * 100) : 0;
                                const itemName = isCatalogData 
                                  ? (item.catalogname || '—')
                                  : (item.posname || '—');
                                const interrupts = isCatalogData
                                  ? (item.avginterruptscount || 0)
                                  : (item.interrupts || 0);
                                return (
                                  <tr key={`${isCatalogData ? item.catalogid : item.posid}-${i}`} className="cs-tr">
                                    <td className="text-center">
                                      <span className="badge badge-primary">{i + 1}</span>
                                    </td>
                                    <td><span className="badge badge-info">{itemName}</span></td>
                                    <td className="text-center">
                                      <span className="text-primary">{secToHHMM(scheduled)}</span>
                                    </td>
                                    <td className="text-center">
                                      <span className="text-success">{secToHHMM(played)}</span>
                                    </td>
                                    <td className="text-center">
                                      <div className="progress" style={{ height: '20px', width: '100px', margin: '0 auto' }}>
                                        <div 
                                          className="progress-bar" 
                                          style={{ width: `${pct}%` }}
                                          aria-valuenow={pct} 
                                          aria-valuemin="0" 
                                          aria-valuemax="100"
                                        >
                                          {pct}%
                                        </div>
                                      </div>
                                    </td>
                                    <td className="text-center">
                                      <span className="text-warning">{fmt(interrupts)}</span>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        ) : (
                          <div className="text-center py-4">
                            <p className="text-muted">No data available for this selection</p>
                          </div>
                        )}
                      </div>
                      <div className="card-footer">
                        <small className="text-muted">
                          SignalLens Analytics · Digital Signage POS Platform | Data period: {periodLabel}
                        </small>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default POSDashboard;
function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
}